import { useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { NotePencil, Plus, Trash } from "@phosphor-icons/react";

import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import McqQuestionEditorModal from "./McqQuestionEditorModal";
import SocraticQuestionEditorModal from "./SocraticQuestionEditorModal";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

import {
  AssessmentType,
  useDeleteMcqQuestionMutation,
  useDeleteSocraticQuestionMutation,
} from "../../../../graphql/generated/graphql";
import type { AiTutor, McqQuestion, Question } from "../../../../graphql/generated/graphql";

type EditorQuestion = McqQuestion | Question;
type SocraticQuestionEntry = {
  tutor: AiTutor;
  question: Question;
};

export type AssessmentQuestionBankModalProps = {
  visible: boolean;
  onClose: () => void;
  isLoading: boolean;
  errorMessage?: string | null;
  assessmentId?: string;
  assessmentType?: AssessmentType | null;
  aiTutors: AiTutor[];
  mcqQuestions?: McqQuestion[];
  onRefresh?: () => Promise<void> | void;
};

const optionLabel = (index: number) => String.fromCharCode(65 + index);
const AssessmentQuestionBankModal = ({
  visible,
  onClose,
  isLoading,
  errorMessage,
  assessmentId,
  assessmentType,
  aiTutors,
  mcqQuestions = [],
  onRefresh,
}: AssessmentQuestionBankModalProps) => {
  const { showToast } = useToast();
  const [deleteMcqQuestion, { loading: isDeletingQuestion }] = useDeleteMcqQuestionMutation();
  const [deleteSocraticQuestion, { loading: isDeletingSocraticQuestion }] = useDeleteSocraticQuestionMutation();
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorType, setEditorType] = useState<"mcq" | "socratic">("mcq");
  const [editingQuestion, setEditingQuestion] = useState<EditorQuestion | null>(null);
  const [editingSocraticQuestionAiTutorId, setEditingSocraticQuestionAiTutorId] = useState<string | null>(null);
  const [isQuestionEditorOpen, setIsQuestionEditorOpen] = useState(false);

  const isMcqAssessment = assessmentType === AssessmentType.Mcq;
  const canManageQuestions = !!assessmentId;
  const flattenedSocraticQuestions = useMemo<SocraticQuestionEntry[]>(() => {
    return aiTutors.flatMap((tutor) =>
      (tutor.questions ?? [])
        .filter((question) => question && question.isDeleted !== true)
        .filter(Boolean)
        .map((question) => ({ tutor, question }))
    );
  }, [aiTutors]);

  const totalQuestions = isMcqAssessment ? mcqQuestions.length : flattenedSocraticQuestions.length;
  const totalIles = aiTutors.length;
  const questionLabel = `question${totalQuestions === 1 ? "" : "s"}`;
  const ileLabel = `ILE${totalIles === 1 ? "" : "s"}`;
  const questionBankSummaryText = `${totalQuestions} ${questionLabel} in bank from ${totalIles} ${ileLabel}`;

  const nextQuestionOrder = useMemo(() => {
    return mcqQuestions.length + 1;
  }, [mcqQuestions]);

  const closeQuestionEditor = () => setIsQuestionEditorOpen(false);

  const handleOpenQuestionEditor = (
    type: "mcq" | "socratic",
    mode: "create" | "edit",
    question?: EditorQuestion,
    selectedTutorId?: string | null
  ) => {
    setEditorMode(mode);
    setEditorType(type);
    setEditingQuestion(question ?? null);
    setEditingSocraticQuestionAiTutorId(selectedTutorId ?? (type === "mcq" ? null : (aiTutors[0]?.id ?? null)));
    setIsQuestionEditorOpen(true);
  };

  const handleOpenCreateQuestionModal = () => {
    handleOpenQuestionEditor(isMcqAssessment ? "mcq" : "socratic", "create");
  };

  const handleOpenEditQuestionModal = (question: McqQuestion) => {
    handleOpenQuestionEditor("mcq", "edit", question);
  };

  const handleOpenEditSocraticQuestionModal = (question: Question, tutor: AiTutor) => {
    handleOpenQuestionEditor("socratic", "edit", question, tutor.id ?? null);
  };

  const handleDeleteQuestion = async (questionType: "mcq" | "socratic", questionId: string) => {
    if (!questionId) {
      return;
    }

    try {
      if (questionType === "mcq") {
        const { data } = await deleteMcqQuestion({
          variables: {
            mcqQuestionId: questionId,
          },
        });

        const response = data?.deleteMcqQuestion;

        if (!response?.success) {
          showToast({
            message: response?.message || "Failed to delete question.",
            type: "error",
          });
          return;
        }
      } else {
        const { data } = await deleteSocraticQuestion({
          variables: {
            questionId,
          },
        });

        const response = data?.deleteSocraticQuestion;

        if (!response?.success) {
          showToast({
            message: response?.message || "Failed to delete question.",
            type: "error",
          });
          return;
        }
      }

      await onRefresh?.();
      showToast({
        message: "Question deleted successfully.",
        type: "success",
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to delete question.",
        type: "error",
      });
    }
  };

  const isDeletingAnyQuestion = isDeletingQuestion || isDeletingSocraticQuestion;
  const socraticQuestionForEditor = editorType === "socratic" ? (editingQuestion as Question | null) : null;
  const canShowCreateQuestionButton = canManageQuestions;

  return (
    <CustomModal visible={visible} onRequestClose={onClose} ariaLabel="Assessment Question Bank">
      <FormLayout
        title="Question Bank"
        isSubmitting={false}
        onCancel={onClose}
        onSubmit={onClose}
        actionText="Back"
        hideCancelButton={true}
        buttonsContainerStyle={{ justifyContent: "flex-end" }}
        contentContainerStyle={{ paddingBottom: 0 }}>
        {isLoading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        ) : (
          <>
            {!!errorMessage && (
              <>
                <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />
                <CustomSpacing size="m" />
              </>
            )}

            {canShowCreateQuestionButton && (
              <>
                <View style={styles.actionsRow}>
                  <CustomButton
                    text="Add Question"
                    useTranslationText={false}
                    styleType="primaryTransparentStroke"
                    size="s"
                    leftIcon={<Plus size={16} color="#3E68FE" />}
                    onPress={handleOpenCreateQuestionModal}
                  />
                </View>
                <CustomSpacing size="m" />
              </>
            )}

            <View style={styles.summaryPill}>
              <CustomText
                text={questionBankSummaryText}
                size="s"
                useTranslationText={false}
                style={styles.summaryText}
                weight="normal"
              />
            </View>

            {isMcqAssessment ? (
              mcqQuestions.length === 0 ? (
                <CustomText
                  text="No MCQ questions found for this assessment."
                  useTranslationText={false}
                  style={styles.emptyText}
                />
              ) : (
                <>
                  {mcqQuestions.map((question, idx) => {
                    return (
                      <View key={question.id ?? `mcq-${idx}`} style={styles.questionCard}>
                        <View style={styles.questionCardHeader}>
                          <View style={styles.questionHeaderTopRow}>
                            <CustomText
                              text={`QUESTION ${idx + 1}`}
                              size="xs"
                              useTranslationText={false}
                              style={styles.questionIndex}
                              weight="normal"
                            />
                            {canManageQuestions && (
                              <View style={styles.questionActions}>
                                <TouchableOpacity
                                  style={styles.questionActionButton}
                                  onPress={() => handleOpenEditQuestionModal(question)}
                                  disabled={isDeletingAnyQuestion}
                                  accessibilityRole="button"
                                  aria-label={`Edit question ${idx + 1}`}>
                                  <NotePencil size={16} color="#667085" />
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={styles.questionActionButton}
                                  onPress={() => {
                                    if (question.id) {
                                      void handleDeleteQuestion("mcq", question.id);
                                    }
                                  }}
                                  disabled={isDeletingAnyQuestion}
                                  accessibilityRole="button"
                                  aria-label={`Delete question ${idx + 1}`}>
                                  <Trash size={16} color="#667085" />
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>
                          <CustomText
                            text={question.stem ?? ""}
                            size="m"
                            useTranslationText={false}
                            style={styles.questionTitle}
                            weight="normal"
                          />
                        </View>

                        <View style={styles.questionCardBody}>
                          <CustomText
                            text="OPTIONS"
                            size="xs"
                            useTranslationText={false}
                            style={styles.sectionLabel}
                            weight="normal"
                          />
                          <CustomSpacing size="s" />

                          <View style={styles.rubricsWrap}>
                            {(question.choices ?? []).map((choice, choiceIndex) => {
                              const isCorrect = choiceIndex === question.correctChoiceIndex;

                              return (
                                <View
                                  key={`${question.id ?? idx}-${choiceIndex}`}
                                  style={[styles.choiceRow, isCorrect && styles.correctChoiceRow]}>
                                  <CustomText
                                    text={`${optionLabel(choiceIndex)}.`}
                                    size="s"
                                    useTranslationText={false}
                                    style={[styles.choiceIndex, isCorrect && styles.correctChoiceIndex]}
                                    weight="normal"
                                  />
                                  <CustomText
                                    text={choice}
                                    size="s"
                                    useTranslationText={false}
                                    style={styles.choiceText}
                                    weight="normal"
                                  />
                                </View>
                              );
                            })}
                          </View>

                          {!!question.explanation && (
                            <>
                              <CustomSpacing size="m" />
                              <CustomText
                                text="EXPLANATION"
                                size="xs"
                                useTranslationText={false}
                                style={styles.sectionLabel}
                                weight="normal"
                              />
                              <CustomSpacing size="xs" />
                              <CustomText
                                text={question.explanation}
                                size="s"
                                useTranslationText={false}
                                style={styles.explanationText}
                                weight="normal"
                              />
                            </>
                          )}

                          <CustomSpacing size="m" />
                          <View style={styles.totalRow}>
                            <CustomText
                              text={`Points: ${question.points ?? 0}`}
                              size="s"
                              useTranslationText={false}
                              style={styles.totalText}
                              weight="normal"
                            />
                          </View>
                        </View>

                        <View style={styles.questionCardFooter}>
                          <CustomText
                            text="Source: Assessment Question Bank"
                            size="s"
                            useTranslationText={false}
                            style={styles.footerText}
                            weight="normal"
                          />
                        </View>
                      </View>
                    );
                  })}
                </>
              )
            ) : flattenedSocraticQuestions.length === 0 ? (
              <CustomText
                text="No socratic questions found for this assessment."
                useTranslationText={false}
                style={styles.emptyText}
              />
            ) : (
              flattenedSocraticQuestions.map(({ tutor, question }, idx) => {
                const socraticQuestion = question;
                const rubrics = socraticQuestion.rubrics ?? [];
                const totalPoints =
                  typeof socraticQuestion.totalScore === "number"
                    ? socraticQuestion.totalScore
                    : rubrics.reduce((s, r) => s + (typeof r.points === "number" ? r.points : 0), 0);

                return (
                  <View key={question.id ?? `${tutor.id}-${idx}`} style={styles.questionCard}>
                    <View style={styles.questionCardHeader}>
                      <View style={styles.questionHeaderTopRow}>
                        <CustomText
                          text={`QUESTION ${idx + 1}`}
                          size="xs"
                          useTranslationText={false}
                          style={styles.questionIndex}
                          weight="normal"
                        />
                        {canManageQuestions && (
                          <View style={styles.questionActions}>
                            <TouchableOpacity
                              style={styles.questionActionButton}
                              onPress={() => handleOpenEditSocraticQuestionModal(socraticQuestion, tutor)}
                              disabled={isDeletingAnyQuestion}
                              accessibilityRole="button"
                              aria-label={`Edit question ${idx + 1}`}>
                              <NotePencil size={16} color="#667085" />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={styles.questionActionButton}
                              onPress={() => {
                                if (question.id) {
                                  void handleDeleteQuestion("socratic", question.id);
                                }
                              }}
                              disabled={isDeletingAnyQuestion}
                              accessibilityRole="button"
                              aria-label={`Delete question ${idx + 1}`}>
                              <Trash size={16} color="#667085" />
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                      <CustomText
                        text={socraticQuestion.question ?? ""}
                        size="m"
                        useTranslationText={false}
                        style={styles.questionTitle}
                        weight="normal"
                      />
                    </View>

                    <View style={styles.questionCardBody}>
                      <CustomText
                        text="RUBRIC CRITERIA"
                        size="xs"
                        useTranslationText={false}
                        style={styles.sectionLabel}
                        weight="normal"
                      />
                      <CustomSpacing size="s" />

                      {rubrics.length === 0 ? (
                        <CustomText
                          text="No rubrics available for this question."
                          useTranslationText={false}
                          style={styles.emptyText}
                        />
                      ) : (
                        <View style={styles.rubricsWrap}>
                          {rubrics.map((r) => (
                            <View key={r.id} style={styles.rubricRow}>
                              <CustomText
                                text={r.component ?? ""}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricText}
                                weight="normal"
                              />
                              <CustomText
                                text={`${r.points ?? 0} pts`}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricPoints}
                                weight="normal"
                              />
                            </View>
                          ))}
                        </View>
                      )}

                      <CustomSpacing size="m" />
                      <View style={styles.totalRow}>
                        <CustomText
                          text={`Total: ${totalPoints} pts`}
                          size="s"
                          useTranslationText={false}
                          style={styles.totalText}
                          weight="normal"
                        />
                      </View>
                    </View>

                    <View style={styles.questionCardFooter}>
                      <CustomText
                        text={`Source: ${tutor.title ?? "Interactive Learning Experience"}`}
                        size="s"
                        useTranslationText={false}
                        style={styles.footerText}
                        weight="normal"
                      />
                    </View>
                  </View>
                );
              })
            )}
          </>
        )}
      </FormLayout>

      <McqQuestionEditorModal
        visible={isQuestionEditorOpen && editorType === "mcq"}
        mode={editorMode}
        assessmentId={assessmentId}
        question={editorType === "mcq" ? (editingQuestion as McqQuestion | null) : null}
        nextOrder={nextQuestionOrder}
        onClose={closeQuestionEditor}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />
      <SocraticQuestionEditorModal
        visible={isQuestionEditorOpen && editorType === "socratic"}
        mode={editorMode}
        aiTutors={aiTutors}
        selectedAiTutorId={editingSocraticQuestionAiTutorId}
        question={socraticQuestionForEditor}
        onClose={closeQuestionEditor}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  errorText: {
    color: "#B42318",
  },
  emptyText: {
    color: "#667085",
  },
  loading: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryPill: {
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: "#F2F4F7",
    marginBottom: 14,
  },
  summaryText: {
    color: "#344054",
  },
  questionCard: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 14,
    backgroundColor: "#FFFFFF",
  },
  questionCardHeader: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  questionHeaderTopRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  questionActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  questionActionButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  questionIndex: {
    color: "#667085",
    letterSpacing: 0.6,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  questionTitle: {
    color: "#101828",
    marginTop: 6,
    lineHeight: 22,
    fontSize: 16,
  },
  questionCardBody: {
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  rubricsWrap: {
    gap: 8,
  },
  sectionLabel: {
    color: "#667085",
    letterSpacing: 0.6,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    backgroundColor: "#EEF4FF",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  rubricText: {
    color: "#101828",
    flex: 1,
  },
  rubricPoints: {
    color: "#3E68FE",
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  correctChoiceRow: {
    backgroundColor: "#ECFDF3",
    borderColor: "#ABEFC6",
  },
  choiceIndex: {
    color: "#667085",
  },
  correctChoiceIndex: {
    color: "#027A48",
  },
  choiceText: {
    color: "#101828",
    flex: 1,
  },
  explanationText: {
    color: "#344054",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  totalText: {
    color: "#344054",
  },
  questionCardFooter: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F9FAFB",
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
  footerText: {
    color: "#344054",
  },
});

export default AssessmentQuestionBankModal;
